import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useArquivosFiscais,
  useBaixarPlanilhaDeArquivosFiscais,
  useBaixarZipDeArquivosFiscais,
  useClientes,
  type FiltroArquivosFiscais,
  type ResultadoDownloadPlanilha,
  type ResultadoDownloadZip,
} from '../../shared/api/queries.ts';
import type {
  ArquivoFiscal,
  Cliente,
  SituacaoFiscal,
} from '../../shared/api/tipos.ts';
import {
  formatarData,
  formatarMoedaDeCentavos,
} from '../../shared/format.ts';
import { DialogoCupom } from './DialogoCupom.tsx';
import { DialogoExclusao } from './DialogoExclusao.tsx';
import { useSessao } from '../auth/sessao.tsx';

/** Primeiro e último dia do mês de uma data, em `AAAA-MM-DD`. */
function limitesDoMes(referencia: Date): { de: string; ate: string } {
  const ano = referencia.getFullYear();
  const mes = referencia.getMonth();

  // Dia 0 do mês seguinte é o último dia deste — sem precisar saber quais
  // meses têm 30, 31 ou 29 dias.
  return { de: emIso(new Date(ano, mes, 1)), ate: emIso(new Date(ano, mes + 1, 0)) };
}

function emIso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

export function ArquivosFiscais() {
  const hoje = new Date();
  const [periodo, setPeriodo] = useState(() => limitesDoMes(hoje));
  const [cliente, setCliente] = useState<Cliente | null>(null);
  // Identificação da nota, digitada. Como as datas, só vira consulta no
  // Pesquisar: a tabela é de dezenas de milhares de cupons por competência.
  const [busca, setBusca] = useState<Busca>({ chave: '', numero: '', serie: '' });
  // "Todas" é o estado de abertura: o cupom cancelado continua no acervo, e
  // esconder por padrão faria sumir da tela uma nota que o PDV emitiu.
  const [situacao, setSituacao] = useState<SituacaoEscolhida>('todas');
  const [zip, setZip] = useState<ResultadoDownloadZip | null>(null);
  const [planilha, setPlanilha] = useState<ResultadoDownloadPlanilha | null>(null);
  const [cupom, setCupom] = useState<ArquivoFiscal | null>(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  /** Quantas acabaram de ser apagadas, para a tela confirmar o que fez. */
  const [excluidas, setExcluidas] = useState<number | null>(null);

  // O que a tabela está mostrando é o filtro **aplicado**, não o que está
  // digitado nos campos. Buscar a cada tecla, numa base de dezenas de milhares
  // de cupons, dispara uma consulta por dígito da data — e todas menos a
  // última são jogadas fora.
  const [filtro, setFiltro] = useState<FiltroArquivosFiscais>(() => ({
    de: limitesDoMes(hoje).de,
    ate: limitesDoMes(hoje).ate,
  }));
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(50);

  const { usuario } = useSessao();
  // Apagar documento fiscal é do administrador, e a rota recusa qualquer
  // outro papel. O botão sai da tela de quem não pode para não oferecer uma
  // porta que responde 403.
  const administrador = usuario?.papel === 'administrador';

  const clientes = useClientes();
  const baixarZip = useBaixarZipDeArquivosFiscais();
  const baixarPlanilha = useBaixarPlanilhaDeArquivosFiscais();

  const { data, isPending, isFetching, isError, error } = useArquivosFiscais(filtro, {
    limite: porPagina,
    deslocamento: pagina * porPagina,
  });

  const arquivos = data?.itens ?? [];
  const resumo = data?.resumo;
  const total = data?.total ?? 0;

  // O cliente do filtro **aplicado**, e não o que está no campo: é o do
  // resultado que está na tela, e é dele que o download e a exclusão tratam.
  // Trocar o campo sem pesquisar não pode mudar o que vai ser apagado.
  const clienteDoFiltro =
    filtro.empresaId === undefined
      ? null
      : (clientes.data?.itens.find((item) => item.id === filtro.empresaId) ?? null);

  // Enquanto não há cliente aplicado, não há o que mostrar: a consulta é
  // sempre de um cliente.
  const semCliente = filtro.empresaId === undefined;
  /** O digitado ainda não virou consulta: a lista na tela é de outro filtro. */
  const filtroPendente = pendente(filtro, cliente, periodo, busca, situacao);

  function pesquisar(evento?: FormEvent) {
    evento?.preventDefault();
    if (cliente === null) return;

    setPagina(0);
    setFiltro({
      empresaId: cliente?.id,
      de: periodo.de,
      ate: periodo.ate,
      ...(situacao === 'todas' ? {} : { situacao }),
      ...semVazios(busca),
    });
  }

  async function baixar() {
    limparAvisos();
    try {
      setZip(await baixarZip.mutateAsync(filtro));
    } catch {
      // A mensagem fica em `baixarZip.error`.
    }
  }

  async function gerarPlanilha() {
    limparAvisos();
    try {
      setPlanilha(await baixarPlanilha.mutateAsync(filtro));
    } catch {
      // A mensagem fica em `baixarPlanilha.error`.
    }
  }

  /** Um aviso por vez: três alertas empilhados escondem a tabela. */
  function limparAvisos() {
    setZip(null);
    setPlanilha(null);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h2">Arquivos fiscais</Typography>
            <Typography variant="body2" color="text.secondary">
              XMLs de NFC-e e NF-e por cliente e período de emissão.
            </Typography>
          </Box>

          {/* Fica longe do Baixar e do Enviar, em vermelho e por último: é a
              única ação da tela que destrói o acervo, e ela existe para
              consertar o que entrou errado — loja cadastrada duas vezes,
              acervo de teste, instalação que subiu o mês do cliente vizinho.
              O diálogo mostra cliente, período e quantidade antes de
              qualquer coisa acontecer. */}
          {administrador && (
            <Tooltip
              title={
                clienteDoFiltro === null || arquivos.length === 0
                  ? 'Pesquise um cliente para poder excluir'
                  : filtroPendente
                    ? 'Clique em Pesquisar: o que vai ser apagado é o filtro aplicado'
                    : 'Apaga as notas do filtro aplicado, sem volta'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverOutlinedIcon />}
                  onClick={() => setConfirmandoExclusao(true)}
                  // Com filtro digitado e não aplicado, a tela mostra uma
                  // situação e a lista é de outra: o diálogo diria "3 notas"
                  // com "Rejeitadas" escrito no campo ao lado — e aqui isso é
                  // apagar o que não se queria.
                  disabled={clienteDoFiltro === null || arquivos.length === 0 || filtroPendente}
                >
                  Excluir notas
                </Button>
              </span>
            </Tooltip>
          )}

          <Button
            variant="outlined"
            startIcon={<GridOnOutlinedIcon />}
            onClick={gerarPlanilha}
            disabled={arquivos.length === 0}
            loading={baixarPlanilha.isPending}
          >
            Gerar planilha
          </Button>

          <Button
            variant="contained"
            startIcon={<FolderZipOutlinedIcon />}
            onClick={baixar}
            disabled={arquivos.length === 0}
            loading={baixarZip.isPending}
          >
            Baixar .zip
          </Button>
        </Toolbar>

        <Divider />

        <CardContent component="form" onSubmit={pesquisar}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center' } }}
          >
            <Autocomplete
              options={clientes.data?.itens ?? []}
              value={cliente}
              onChange={(_, escolhido) => setCliente(escolhido)}
              getOptionLabel={(opcao) => opcao.razaoSocial}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={clientes.isPending}
              noOptionsText="Nenhum cliente cadastrado."
              sx={{ minWidth: { md: 320 }, flexGrow: 1 }}
              renderInput={(parametros) => (
                <TextField
                  {...parametros}
                  label="Cliente"
                  required
                  placeholder="Escolha o cliente"
                  helperText="A consulta é sempre de um cliente."
                />
              )}
              renderOption={(parametros, opcao) => {
                const { key, ...resto } = parametros;
                return (
                  <li key={key} {...resto}>
                    <Stack>
                      <Typography variant="body2">{opcao.razaoSocial}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opcao.cnpjFormatado}
                      </Typography>
                    </Stack>
                  </li>
                );
              }}
            />

            <TextField
              label="De"
              type="date"
              value={periodo.de}
              onChange={(evento) =>
                setPeriodo((atual) => ({ ...atual, de: evento.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Até"
              type="date"
              value={periodo.ate}
              onChange={(evento) =>
                setPeriodo((atual) => ({ ...atual, ate: evento.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button size="small" onClick={() => setPeriodo(limitesDoMes(new Date()))}>
                Mês atual
              </Button>
              <Button
                size="small"
                onClick={() =>
                  setPeriodo(
                    limitesDoMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
                  )
                }
              >
                Mês anterior
              </Button>
            </Stack>
          </Stack>

          {/* Segunda linha: achar uma nota específica. Um campo por pergunta —
              número e série juntos são o que identifica a nota, e a chave
              sozinha identifica sem ambiguidade. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 2, alignItems: { md: 'center' } }}
          >
            <TextField
              label="Chave de acesso"
              value={busca.chave}
              onChange={(evento) =>
                setBusca((atual) => ({ ...atual, chave: evento.target.value }))
              }
              placeholder="Inteira ou um pedaço"
              sx={{ flexGrow: 1, minWidth: { md: 320 } }}
            />
            <TextField
              label="Número"
              value={busca.numero}
              onChange={(evento) =>
                setBusca((atual) => ({ ...atual, numero: evento.target.value }))
              }
              inputMode="numeric"
              sx={{ width: { md: 160 } }}
            />
            <TextField
              label="Série"
              value={busca.serie}
              onChange={(evento) =>
                setBusca((atual) => ({ ...atual, serie: evento.target.value }))
              }
              inputMode="numeric"
              sx={{ width: { md: 120 } }}
            />

            {/* A situação é relatada pelo client da loja, pela chave. Quem
                está conferindo o fechamento quer as autorizadas; quem está
                investigando uma diferença quer justamente as canceladas, e
                quem está atrás de nota que não subiu quer as pendentes e as
                rejeitadas — com o motivo que a SEFAZ deu. */}
            <TextField
              select
              label="Situação"
              value={situacao}
              onChange={(evento) =>
                setSituacao(evento.target.value as SituacaoEscolhida)
              }
              sx={{ width: { md: 180 } }}
            >
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="autorizada">Autorizadas</MenuItem>
              <MenuItem value="cancelada">Canceladas</MenuItem>
              <MenuItem value="pendente">Pendentes</MenuItem>
              <MenuItem value="rejeitada">Rejeitadas</MenuItem>
            </TextField>
            {/* Sem cliente não há consulta a fazer, e o botão diz isso antes
                de alguém clicar e receber um erro do servidor. */}
            <Tooltip title={cliente === null ? 'Escolha o cliente primeiro.' : ''}>
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchOutlinedIcon />}
                  disabled={cliente === null}
                  loading={isFetching}
                >
                  Pesquisar
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {filtroPendente && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              O filtro mudou. Clique em Pesquisar para atualizar a lista.
            </Typography>
          )}
        </CardContent>
      </Card>

      {baixarZip.isError && <Alert severity="error">{mensagemDoErro(baixarZip.error)}</Alert>}

      {baixarPlanilha.isError && (
        <Alert severity="error">{mensagemDoErro(baixarPlanilha.error)}</Alert>
      )}

      {planilha !== null && (
        <Alert severity="success" onClose={() => setPlanilha(null)}>
          {planilha.nome} baixado com {planilha.incluidos} nota(s) e o total somado no fim.
        </Alert>
      )}

      {excluidas !== null && (
        <Alert severity="info" onClose={() => setExcluidas(null)}>
          {excluidas} nota(s) excluída(s). O registro de quem apagou, quando e
          por quê fica no servidor.
        </Alert>
      )}

      {zip !== null && (
        <Alert severity={zip.ausentes > 0 ? 'warning' : 'success'} onClose={() => setZip(null)}>
          {zip.ausentes > 0
            ? `${zip.nome} baixado com ${zip.incluidos} arquivo(s); ${zip.ausentes} não foram encontrados no servidor.`
            : `${zip.nome} baixado com ${zip.incluidos} arquivo(s).`}
        </Alert>
      )}

      <Card>
        <CardContent>
          {semCliente ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 6, textAlign: 'center' }}
            >
              Escolha um cliente e clique em Pesquisar. As notas são consultadas sempre
              por cliente — e o <code>.zip</code> de todos juntos misturaria documento
              fiscal de lojas diferentes num arquivo só.
            </Typography>
          ) : isError ? (
            <Alert severity="error">
              Não foi possível carregar os arquivos. {mensagemDoErro(error)}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1}>
              {Array.from({ length: 5 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={40} />
              ))}
            </Stack>
          ) : arquivos.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 6, textAlign: 'center' }}
            >
              Nenhum XML neste período. As notas chegam pelo client instalado na loja —
              confira o filtro ou as instalações do cliente.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Documento</TableCell>
                    <TableCell>Chave de acesso</TableCell>
                    <TableCell align="right">Número</TableCell>
                    <TableCell align="right">Série</TableCell>
                    <TableCell>Emissão</TableCell>
                    <TableCell>Operação</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Cupom</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {arquivos.map((arquivo) => (
                    <TableRow key={arquivo.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {/* Modelo à vista: é o que diferencia a nota de
                            consumidor, que vem do client, da NF-e enviada
                            à mão pelo escritório. */}
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={arquivo.modelo === '65' ? 'NFC-e' : 'NF-e'}
                          />
                          {/* O que não está autorizado continua na lista: o
                              cupom cancelado existiu, e a nota pendente ou
                              rejeitada é justamente a que alguém precisa
                              encontrar. Todas somem do total sem sumir do
                              acervo. */}
                          {arquivo.situacao !== 'autorizada' && (
                            <Chip
                              size="small"
                              color={CORES_DA_SITUACAO[arquivo.situacao]}
                              label={ROTULOS_DA_SITUACAO[arquivo.situacao]}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                        >
                          {arquivo.chave}
                        </Typography>
                        {/* O motivo fica embaixo da chave, e não em coluna
                            própria: é texto da SEFAZ, de tamanho imprevisível,
                            e só existe para as poucas linhas que não estão
                            autorizadas — uma coluna a mais estreitaria a
                            tabela inteira para mostrar vazio na maioria das
                            linhas. Aqui ele nasce ao lado do identificador que
                            se usa para cobrar a nota da loja. */}
                        {arquivo.motivo !== null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', maxWidth: 420 }}
                          >
                            {arquivo.motivo}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {arquivo.numero}
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number(arquivo.serie)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatarData(arquivo.emitidoEm)}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={arquivo.operacao === 'entrada' ? 'Entrada' : 'Saída'}
                          color={arquivo.operacao === 'entrada' ? 'default' : 'primary'}
                          variant={arquivo.operacao === 'entrada' ? 'outlined' : 'filled'}
                        />
                      </TableCell>

                      {/* Riscado porque este valor não entra na soma do
                          rodapé: ler a coluna de cima a baixo e não bater com
                          o total é o tipo de diferença que custa uma tarde. */}
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                          ...(arquivo.situacao === 'autorizada'
                            ? {}
                            : { textDecoration: 'line-through', color: 'text.disabled' }),
                        }}
                      >
                        {formatarMoedaDeCentavos(arquivo.valorCentavos)}
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Ver o cupom">
                          <IconButton
                            size="small"
                            onClick={() => setCupom(arquivo)}
                            aria-label={`Ver o cupom da nota ${arquivo.numero}`}
                          >
                            <ReceiptLongOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, nova) => setPagina(nova)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(evento) => {
              setPorPagina(Number(evento.target.value));
              setPagina(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Card>

      <DialogoCupom arquivo={cupom} aoFechar={() => setCupom(null)} />

      {confirmandoExclusao && clienteDoFiltro !== null && filtro.empresaId !== undefined && (
        <DialogoExclusao
          cliente={clienteDoFiltro}
          filtro={{ ...filtro, empresaId: filtro.empresaId }}
          quantidade={resumo?.quantidade ?? total}
          aoFechar={() => setConfirmandoExclusao(false)}
          aoExcluido={(excluidos) => {
            // O zip baixado fala de notas que podem não existir mais: apagar
            // o aviso é mais honesto que deixá-lo na tela.
            setZip(null);
            setExcluidas(excluidos);
          }}
        />
      )}

      {resumo !== undefined && resumo.quantidade > 0 && (
        <Stack
          direction="row"
          spacing={3}
          sx={{ flexWrap: 'wrap', px: 1, color: 'text.secondary' }}
        >
          <Typography variant="body2">
            <strong>{resumo.quantidade}</strong> nota(s) no período
          </Typography>
          <Typography variant="body2">
            Entradas: <strong>{formatarMoedaDeCentavos(resumo.entradasCentavos)}</strong>
          </Typography>
          <Typography variant="body2">
            Saídas: <strong>{formatarMoedaDeCentavos(resumo.saidasCentavos)}</strong>
          </Typography>
          {/* Três contadores, porque são três perguntas: o cancelamento é
              fato consumado, a pendente espera a SEFAZ e a rejeitada precisa
              de alguém para resolvê-la. */}
          {resumo.canceladas > 0 && (
            <Typography variant="body2" color="error.main">
              <strong>{resumo.canceladas}</strong> cancelada(s), fora das somas
            </Typography>
          )}
          {resumo.pendentes > 0 && (
            <Typography variant="body2" color="warning.main">
              <strong>{resumo.pendentes}</strong> pendente(s), fora das somas
            </Typography>
          )}
          {resumo.rejeitadas > 0 && (
            <Typography variant="body2" color="error.main">
              <strong>{resumo.rejeitadas}</strong> rejeitada(s), fora das somas
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * O filtro digitado já é o filtro aplicado?
 *
 * Sem este aviso, mudar a data e não clicar em Pesquisar deixa a tela
 * mostrando números de outro período com os campos dizendo outra coisa — e
 * quem confere fecha o mês com o total errado.
 */
function pendente(
  aplicado: FiltroArquivosFiscais,
  cliente: Cliente | null,
  periodo: { de: string; ate: string },
  busca: Busca,
  situacao: SituacaoEscolhida,
): boolean {
  return (
    (aplicado.situacao ?? 'todas') !== situacao ||
    (aplicado.empresaId ?? undefined) !== (cliente?.id ?? undefined) ||
    aplicado.de !== periodo.de ||
    aplicado.ate !== periodo.ate ||
    (aplicado.chave ?? '') !== busca.chave.trim() ||
    (aplicado.numero ?? '') !== busca.numero.trim() ||
    (aplicado.serie ?? '') !== busca.serie.trim()
  );
}

/** O que o seletor de situação oferece; "todas" não vira filtro nenhum. */
type SituacaoEscolhida = 'todas' | SituacaoFiscal;

/**
 * O rótulo e a cor de cada situação, num lugar só.
 *
 * `autorizada` entra nos dois mapas para o tipo ficar fechado, mas a tela não
 * desenha chip para ela: a nota que simplesmente vale é a regra, e marcar a
 * regra encheria a coluna de etiqueta repetida.
 */
const ROTULOS_DA_SITUACAO: Record<SituacaoFiscal, string> = {
  autorizada: 'Autorizada',
  cancelada: 'Cancelada',
  pendente: 'Pendente',
  rejeitada: 'Rejeitada',
};

const CORES_DA_SITUACAO: Record<SituacaoFiscal, 'default' | 'error' | 'warning'> = {
  autorizada: 'default',
  cancelada: 'error',
  // Pendente é espera, não erro: ela costuma se resolver sozinha quando a
  // SEFAZ responde, e pintá-la de vermelho faria o escritório correr atrás do
  // que ainda vai acontecer.
  pendente: 'warning',
  rejeitada: 'error',
};

/** Os três campos de identificação da nota, como estão digitados. */
interface Busca {
  readonly chave: string;
  readonly numero: string;
  readonly serie: string;
}

/**
 * Campo em branco não entra no filtro.
 *
 * Mandar `chave=''` faria a tela dizer que o filtro aplicado tem uma chave
 * vazia, e o aviso de "o filtro mudou" ficaria aceso para sempre.
 */
function semVazios(busca: Busca): Partial<Busca> {
  return Object.fromEntries(
    Object.entries(busca)
      .map(([campo, valor]) => [campo, valor.trim()])
      .filter(([, valor]) => valor !== ''),
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
